import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Icon from './Icon';

import ThemeContext from '../contexts/ThemeContext';

import { v4 as uuid } from 'uuid'; 

const StyledIcon = styled(Icon)`
  & > i, & > span {
    color: ${({ color }) => (color)} !important;
  }
`;

const StyledRequiredIcon = styled(Icon)`
  margin-right: 10px;
  & > i, & > span {
    color: ${({ color }) => (color)} !important;
  }
`;

const RowBlock = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  background-color: transparent;
  ${({ padding, noPaddings }) => (noPaddings ? '' : `padding: ${padding * 2}px ${padding * 2}px`)};
`;

const ColBlock = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background-color: transparent;
  margin-left: ${({ isSetMarginLeft, margin }) => (isSetMarginLeft ? margin : 0)}px;
  margin-right: ${({ isSetMarginRight, margin }) => (isSetMarginRight ? margin: 45)}px;
  min-height: ${({ subtitle, lineHeight }) => (
    !subtitle ? 25 : (lineHeight || 48)
  )}px;
`;

const Title = styled.div`
  font-size: ${({ fontSize }) => (fontSize)}px;
  text-align: ${({ alignCenter }) => (alignCenter ? 'center' : 'left')};
  color: ${({ color }) => (color)};
  background-color: transparent;
  margin-bottom: ${({ subtitle }) => (subtitle ? '5px' : '0')};
  line-height: 18px;
`;

const Subtitle = styled.div`
  font-size: ${({ fontSize }) => (fontSize)}px;
  text-align: ${({ alignCenter }) => (alignCenter ? 'center' : 'left')};
  color: ${({ color }) => (color)};
  background-color: transparent;
  word-break: break-word;
`;

const StyledDevider = styled.div`
  height: 1px;
  width: 100%;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
`;

const ListItemContainer = styled.div`
  flex: 1;
  flex-direction: row;
  ${({ alignCenter }) => (alignCenter ? 'text-align: center;' : '')};
  background-color: ${({ selected, selectedColor }) => (selected ? selectedColor : 'transparent')};
  border-radius: 6px;
  transition: ${({ transition }) => (transition)};
  overflow: hidden;
  ${({ readOnly, selectedColor }) => (!readOnly ? `
      cursor: pointer;
      &:hover {
        background-color: ${selectedColor};
        & ${Title} {
          color: white;
        }
        & ${StyledIcon} > i, & ${StyledIcon} > span {
          color: white !important;
        }
      }
    ` : ''
  )}
  &:last-child {
    & ${StyledDevider} {
      border-color: transparent !important;
    }
  }
`;

const ActionListItem = (props) => {
  const themeContext = useContext(ThemeContext);

  const colors = themeContext.colors;
  const layout = themeContext.layout;
  const hasSubtitle = props.subtitle || props.subtitleComponent;

  return (
    <ListItemContainer
      key={() => uuid()}
      data-test={props['data-test']}
      readOnly={props.readOnly}
      transition={layout.transition}
      selected={props.selected}
      selectedColor={colors.tulkaMainColor}
      onClick={(data) => {
        if (!props.readOnly) {
          props.onClick(data);
        }
      }}
    >
      <RowBlock
        padding={layout.padding}
        noPaddings={props.noPaddings}
      >
        {props.icon && <StyledIcon
          color={props.selected ? 'white' : colors.webDarkBlue}
          margin={layout.margin}
          iconLib={props.iconLib}
          icon={props.icon}
          iconColor={props.iconColor}
        />}
        <ColBlock
          isSetMarginLeft={props.icon}
          isSetMarginRight={props.selected}
          margin={layout.margin}
          subtitle={hasSubtitle}
          lineHeight={props.lineHeight}
        >
          {props.title ? (
            <Title
              alignCenter={props.alignCenter}
              fontSize={layout.fontSize}
              subtitle={hasSubtitle}
              color={props.selected ? 'white' : colors.webDarkBlue}
            >
              {props.title}
            </Title>
          ) : null}
          {props.subtitleComponent ? (
            <div>
              {props.subtitleComponent}
            </div>
          ) : null}
          {(!props.subtitleComponent && props.subtitle) ?
            Array.isArray(props.subtitle) ?
              props.subtitle.map(sub => (
                <Subtitle
                  key={sub}
                  alignCenter={props.alignCenter}
                  fontSize={layout.mediumFontSize}
                  color={colors.webGray}
                >
                  {sub}
                </Subtitle>
              )) : (
            <Subtitle
              alignCenter={props.alignCenter}
              fontSize={layout.mediumFontSize}
              color={colors.webGray}
            >
              {props.subtitle}
            </Subtitle>
          ) : null}
        </ColBlock>
        {props.selected ? (
          <StyledIcon
            color={'white'}
            margin={layout.margin}
            iconLib={'material'}
            icon={'check'}
          />
        ) : null}
        {props.required ? (
          <StyledRequiredIcon
            color={colors.error}
            margin={layout.margin}
            iconLib={'material'}
            icon={'warning_amber'}
          />
        ) : null}
        {props.editable ? (
          <StyledIcon
            margin={layout.margin}
            iconLib={'material'}
            icon={'edit'}
          />
        ) : null}
      </RowBlock>
      <StyledDevider />
    </ListItemContainer>
  );
};

ActionListItem.propTypes = {
  'data-test': PropTypes.string,
  readOnly: PropTypes.bool,
  selected: PropTypes.bool,
  required: PropTypes.bool,
  alignCenter: PropTypes.bool,
  noPaddings: PropTypes.bool,
  title: PropTypes.string,
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  subtitleComponent: PropTypes.node,
  lineHeight: PropTypes.number,
  icon: PropTypes.string,
  iconLib: PropTypes.string,
  iconColor: PropTypes.string,
  onClick: PropTypes.func,
};

ActionListItem.defaultProps = {
  readOnly: false,
  selected: false,
  required: false,
  alignCenter: false,
  noPaddings: false,
  onClick: () => {},
};

export default ActionListItem;
